import React, { useState, useRef, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency, formatMoney, formatDate } from '../../utils/formatters';
import { toBRL } from '../../utils/exchange';
import { parseOFX, parseCSV } from '../../utils/importers';
import { toast } from '../shared/Toast';
import { v4 as uuidv4 } from 'uuid';
import TransactionModal from '../Transactions/TransactionModal';

const accountGradients = {
  '#0d1b2a': 'linear-gradient(135deg,#0d1b2a,#1e3a52)',
  '#8A05BE': 'linear-gradient(135deg,#5c0381,#8A05BE)',
  '#1D9E75': 'linear-gradient(135deg,#0F6E56,#1D9E75)',
  '#185FA5': 'linear-gradient(135deg,#0c447c,#378ADD)',
  'var(--gold)': 'linear-gradient(135deg,#8B6914,var(--gold))',
  '#A32D2D': 'linear-gradient(135deg,#791F1F,#e24b4a)',
};

function AccountModal({ account, onClose }) {
  const { state, dispatch } = useApp();
  const [form, setForm] = useState(account || { name: '', type: 'corrente', currency: 'BRL', balance: '', overdraft: '', color: '#0d1b2a', active: true });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isUSD = form.currency === 'USD';
  const save = () => {
    if (!form.name.trim()) return;
    const payload = { ...form, currency: form.currency || 'BRL', balance: parseFloat(form.balance) || 0, overdraft: isUSD ? 0 : (parseFloat(form.overdraft) || 0) };
    if (account) dispatch({ type: 'UPDATE_ACCOUNT', payload });
    else dispatch({ type: 'ADD_ACCOUNT', payload: { id: uuidv4(), ...payload } });
    toast.success(account ? 'Conta atualizada!' : 'Conta criada!');
    onClose();
  };
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">{account ? 'Editar conta' : 'Nova conta'}<button className="close-btn" onClick={onClose}>×</button></div>
        <div className="form-group"><label className="form-label">Nome da conta</label><input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Itaú C/C" autoFocus /></div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Tipo</label>
            <select className="form-control" value={form.type} onChange={e => set('type', e.target.value)}>
              <option value="corrente">Conta corrente</option><option value="poupanca">Poupança</option><option value="investimento">Investimento</option>
            </select>
          </div>
          <div className="form-group"><label className="form-label">Moeda</label>
            <select className="form-control" value={form.currency || 'BRL'} onChange={e => set('currency', e.target.value)}>
              <option value="BRL">Real (R$)</option><option value="USD">Dólar (US$)</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Saldo ({isUSD ? 'US$' : 'R$'})</label><input className="form-control" type="number" value={form.balance} onChange={e => set('balance', e.target.value)} placeholder="0,00" /></div>
          {!isUSD && (
            <div className="form-group">
              <label className="form-label">Cheque Especial (R$) <span style={{ color: 'var(--muted)', fontWeight: 400 }}>opcional</span></label>
              <input className="form-control" type="number" min="0" value={form.overdraft} onChange={e => set('overdraft', e.target.value)} placeholder="0,00" />
            </div>
          )}
        </div>
        {isUSD && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: -6, marginBottom: 14 }}>
            <i className="fas fa-circle-info" style={{ marginRight: 6 }}></i>
            Conta em dólar: o saldo e os lançamentos dela ficam em US$. Nos totais do app, o valor é convertido pra R$ pelo câmbio do dia (hoje R$ {state.usdRate.toFixed(2).replace('.', ',')}).
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Cor</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.keys(accountGradients).map(c => (
              <div key={c} onClick={() => set('color', c)} style={{ width: 28, height: 28, borderRadius: '50%', background: c, cursor: 'pointer', border: form.color === c ? '2px solid var(--gold)' : '2px solid transparent', transition: 'transform 0.1s' }} />
            ))}
          </div>
        </div>
        <button className="btn btn-primary btn-full" onClick={save} style={{ padding: 12 }}><i className="fas fa-check"></i> Salvar</button>
      </div>
    </div>
  );
}

function CardModal({ card, onClose }) {
  const { dispatch } = useApp();
  const [form, setForm] = useState(card || { name: '', brand: 'visa', lastDigits: '', limit: '', used: 0, dueDay: 10, closeDay: 3 });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = () => {
    if (!form.name.trim()) return;
    const payload = { ...form, limit: parseFloat(form.limit) || 0, used: parseFloat(form.used) || 0 };
    if (card) {
      // Preserva card.used acumulado pelas transações — não deixa o form sobrescrever
      dispatch({ type: 'UPDATE_CARD', payload: { ...payload, used: card.used } });
    } else {
      dispatch({ type: 'ADD_CARD', payload: { id: uuidv4(), ...payload } });
    }
    toast.success(card ? 'Cartão atualizado!' : 'Cartão adicionado!');
    onClose();
  };
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">{card ? 'Editar cartão' : 'Novo cartão'}<button className="close-btn" onClick={onClose}>×</button></div>
        <div className="form-group"><label className="form-label">Nome</label><input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Nubank Roxinho" autoFocus /></div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Bandeira</label>
            <select className="form-control" value={form.brand} onChange={e => set('brand', e.target.value)}>
              <option value="visa">Visa</option><option value="mastercard">Mastercard</option><option value="elo">Elo</option><option value="amex">Amex</option>
            </select>
          </div>
          <div className="form-group"><label className="form-label">Últimos 4 dígitos</label><input className="form-control" maxLength={4} value={form.lastDigits} onChange={e => set('lastDigits', e.target.value)} placeholder="0000" /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Limite (R$)</label><input className="form-control" type="number" value={form.limit} onChange={e => set('limit', e.target.value)} /></div>
          <div className="form-group">
            <label className="form-label">
              Fatura atual (R$)
              {card && <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10, marginLeft: 6 }}>calculado automaticamente</span>}
            </label>
            <input
              className="form-control"
              type="number"
              value={card ? card.used : form.used}
              onChange={e => !card && set('used', e.target.value)}
              readOnly={!!card}
              style={card ? { background: 'var(--surface2)', color: 'var(--muted)', cursor: 'not-allowed' } : {}}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Dia vencimento</label><input className="form-control" type="number" min="1" max="31" value={form.dueDay} onChange={e => set('dueDay', parseInt(e.target.value))} /></div>
          <div className="form-group"><label className="form-label">Dia fechamento</label><input className="form-control" type="number" min="1" max="31" value={form.closeDay} onChange={e => set('closeDay', parseInt(e.target.value))} /></div>
        </div>
        <button className="btn btn-primary btn-full" onClick={save} style={{ padding: 12 }}><i className="fas fa-check"></i> Salvar</button>
      </div>
    </div>
  );
}

function ImportModal({ onClose }) {
  const { state, dispatch } = useApp();
  const [preview, setPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [accountId, setAccountId] = useState(state.accounts[0]?.id || '');
  const [error, setError] = useState('');
  const fileRef = useRef();

  const processFile = (file) => {
    setError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      let parsed = [];
      try {
        if (file.name.toLowerCase().endsWith('.ofx') || file.name.toLowerCase().endsWith('.qfx')) {
          parsed = parseOFX(text);
        } else if (file.name.toLowerCase().endsWith('.csv')) {
          parsed = parseCSV(text);
        } else { setError('Formato não suportado. Use OFX ou CSV.'); return; }
        if (parsed.length === 0) { setError('Nenhuma transação encontrada no arquivo.'); return; }
        setPreview({ transactions: parsed.map(t => ({ ...t, accountId })), filename: file.name });
      } catch (err) { setError('Erro ao processar arquivo: ' + err.message); }
    };
    reader.readAsText(file, 'ISO-8859-1');
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) processFile(f); };
  const onFileChange = (e) => { const f = e.target.files[0]; if (f) processFile(f); };
  const confirmImport = () => {
    dispatch({ type: 'IMPORT_TRANSACTIONS', payload: preview.transactions.map(t => ({ ...t, accountId })) });
    toast.success(`${preview.transactions.length} transações importadas!`);
    onClose();
  };
  const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title"><span><i className="fas fa-upload" style={{ marginRight: 8, color: 'var(--blue)' }}></i>Importar extrato</span><button className="close-btn" onClick={onClose}>×</button></div>

        {!preview ? (
          <>
            <div className="form-group">
              <label className="form-label">Conta destino</label>
              <select className="form-control" value={accountId} onChange={e => setAccountId(e.target.value)}>
                {state.accounts.filter(a => a.active).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className={`upload-zone ${dragging ? 'dragging' : ''}`}
              onDrop={onDrop} onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
              onClick={() => fileRef.current?.click()}>
              <i className="fas fa-cloud-upload-alt" style={{ fontSize: 32, color: 'var(--muted)', display: 'block', marginBottom: 10 }}></i>
              <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>Arraste o arquivo aqui ou clique para selecionar</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>Suporte: OFX · CSV</div>
              <input ref={fileRef} type="file" accept=".ofx,.qfx,.csv" style={{ display: 'none' }} onChange={onFileChange} />
            </div>
            {error && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 10, padding: '8px 12px', background: 'rgba(226,75,74,0.08)', borderRadius: 'var(--radius-sm)' }}>{error}</div>}
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>
              <strong>Formato CSV esperado:</strong> Data;Descrição;Valor<br/>
              <span style={{ opacity: 0.7 }}>Ex: 01/06/2026;Supermercado;-423.00</span>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{preview.filename}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{preview.transactions.length} transações encontradas</div>
              </div>
              <button onClick={() => setPreview(null)} className="btn btn-ghost btn-sm"><i className="fas fa-arrow-left"></i> Voltar</button>
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto', marginBottom: 14, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              <table className="import-table">
                <thead><tr><th>Data</th><th>Descrição</th><th>Valor</th></tr></thead>
                <tbody>
                  {preview.transactions.slice(0, 20).map((t, i) => (
                    <tr key={i}>
                      <td>{t.date}</td>
                      <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</td>
                      <td style={{ color: t.type === 'income' ? 'var(--green)' : 'var(--red)', fontWeight: 500 }}>{t.type === 'income' ? '+' : '-'}{fmt(t.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.transactions.length > 20 && <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10, textAlign: 'center' }}>+ {preview.transactions.length - 20} transações não exibidas</div>}
            <button className="btn btn-primary btn-full" onClick={confirmImport} style={{ padding: 12 }}>
              <i className="fas fa-file-import"></i> Importar {preview.transactions.length} transações
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const MONTH_NAMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

function AccountStatement({ account, onClose }) {
  const { state } = useApp();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [showTxModal, setShowTxModal] = useState(false);

  const cats = useMemo(() => Object.fromEntries(state.categories.map(c => [c.id, c])), [state.categories]);

  const txs = useMemo(() => {
    const mm = String(month + 1).padStart(2, '0');
    const prefix = `${year}-${mm}`;
    return state.transactions
      .filter(t => t.accountId === account.id && !t.cardId && t.date?.startsWith(prefix))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [state.transactions, account.id, year, month]);

  const totalIn  = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.value, 0);
  const totalOut = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.value, 0);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const grad = accountGradients[account.color] || 'linear-gradient(135deg,#444,#888)';

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 540, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-handle"></div>

        {/* Header colorido */}
        <div style={{ background: grad, borderRadius: '12px 12px 0 0', padding: '18px 20px 16px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{typeLabel[account.type] || 'Conta'}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginTop: 2 }}>{account.name}</div>
            </div>
            <button className="close-btn" onClick={onClose} style={{ color: '#fff', opacity: 0.8 }}>×</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Saldo atual</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: account.balance < 0 ? '#fca5a5' : '#fff' }}>{formatMoney(account.balance, account.currency)}</div>
            </div>
            <button
              onClick={() => setShowTxModal(true)}
              style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            >
              <i className="fas fa-plus" style={{ fontSize: 12 }}></i> Novo lançamento
            </button>
          </div>
        </div>

        {/* Seletor de mês */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <button className="btn btn-ghost btn-sm" onClick={prevMonth} style={{ padding: '4px 10px' }}><i className="fas fa-chevron-left"></i></button>
          <div style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 14 }}>{MONTH_NAMES[month]} {year}</div>
          <button className="btn btn-ghost btn-sm" onClick={nextMonth} style={{ padding: '4px 10px' }}><i className="fas fa-chevron-right"></i></button>
        </div>

        {/* Resumo do mês */}
        {(() => {
          const saldoInicial = account.balance - (totalIn - totalOut);
          return (
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: 'var(--muted)' }}>
                <span>Saldo inicial do mês</span>
                <span style={{ fontWeight: 600, color: saldoInicial >= 0 ? 'var(--text)' : 'var(--red)' }}>{formatMoney(saldoInicial, account.currency)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: 'var(--muted)' }}>
                <span>+ Entradas</span>
                <span style={{ fontWeight: 600, color: 'var(--green)' }}>+{formatMoney(totalIn, account.currency)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: 'var(--muted)' }}>
                <span>− Saídas</span>
                <span style={{ fontWeight: 600, color: 'var(--red)' }}>-{formatMoney(totalOut, account.currency)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0 2px', borderTop: '1px solid var(--border)', marginTop: 4 }}>
                <span style={{ fontWeight: 700 }}>Saldo atual</span>
                <span style={{ fontWeight: 800, color: account.balance >= 0 ? 'var(--green)' : 'var(--red)' }}>{formatMoney(account.balance, account.currency)}</span>
              </div>
            </div>
          );
        })()}

        {/* Lista de transações */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px' }}>
          {txs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
              <i className="fas fa-receipt" style={{ fontSize: 28, marginBottom: 10, display: 'block' }}></i>
              Nenhum lançamento em {MONTH_NAMES[month]}
            </div>
          ) : txs.map(tx => {
            const cat = cats[tx.categoryId];
            const isLate = tx.status === 'pending' && new Date(tx.date + 'T00:00:00') < today;
            return (
              <div key={tx.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 0', borderBottom: '1px solid var(--border)',
              }}>
                {/* Ícone categoria */}
                <div style={{
                  width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                  background: cat ? `${cat.color}18` : 'var(--surface2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: cat?.color || 'var(--muted)', fontSize: 13,
                }}>
                  <i className={`fas ${cat?.icon || 'fa-tag'}`}></i>
                </div>
                {/* Descrição */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.description}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                    {formatDate(tx.date)}
                    {cat && <> · {cat.name}</>}
                  </div>
                </div>
                {/* Status tag */}
                {tx.status === 'paid'
                  ? <span className="tag tag-green" style={{ fontSize: 10, flexShrink: 0 }}>Pago</span>
                  : isLate
                    ? <span className="tag tag-red" style={{ fontSize: 10, flexShrink: 0 }}>Atrasado</span>
                    : <span className="tag tag-amber" style={{ fontSize: 10, flexShrink: 0 }}>Pendente</span>
                }
                {/* Valor — na moeda da conta */}
                <div style={{ fontWeight: 700, fontSize: 14, color: tx.type === 'income' ? 'var(--green)' : 'var(--red)', flexShrink: 0 }}>
                  {tx.type === 'income' ? '+' : '-'}{formatMoney(tx.value, account.currency)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {showTxModal && (
        <TransactionModal
          tx={null}
          defaultAccountId={account.id}
          onClose={() => setShowTxModal(false)}
        />
      )}
    </div>
  );
}

const typeLabel = { corrente: 'Conta Corrente', poupanca: 'Conta Poupança', investimento: 'Conta Investimento' };
const brandLabel = { visa: 'Visa', mastercard: 'Mastercard', elo: 'Elo', amex: 'Amex' };
const brandColor = { visa: '#1a1f71', mastercard: '#eb001b', elo: '#ffcb05', amex: '#007bc1' };
const brandGrad = {
  mastercard: 'linear-gradient(135deg,#eb001b,#f79e1b)',
  visa:       'linear-gradient(135deg,#1a1f71,#4a5abd)',
  elo:        'linear-gradient(135deg,#111,#444)',
  amex:       'linear-gradient(135deg,#007bc1,#00b9f2)',
};

export function PayBillModal({ card, onClose }) {
  const { state, dispatch } = useApp();
  // Fatura de cartão é sempre em R$ — contas em dólar ficam fora pra não
  // debitar US$ como se fosse R$ sem conversão
  const payAccounts = state.accounts.filter(a => a.currency !== 'USD');
  const [amount, setAmount] = useState(String(card.used || ''));
  const [accountId, setAccountId] = useState(payAccounts[0]?.id || '');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const pay = () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error('Informe o valor do pagamento.'); return; }
    if (!accountId) { toast.error('Selecione a conta de pagamento.'); return; }
    dispatch({ type: 'PAY_CARD_BILL', payload: { cardId: card.id, amount: val, accountId, date } });
    toast.success(`Fatura de ${formatCurrency(val)} paga!`);
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-handle"></div>
        <div className="modal-title">
          <span><i className="fas fa-money-bill-wave" style={{ marginRight: 8, color: '#1d9e75' }}></i>Pagar Fatura</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div style={{ padding: '4px 0 12px', fontSize: 13, color: 'var(--muted)' }}>
          Fatura atual: <strong style={{ color: 'var(--red)' }}>{formatCurrency(card.used)}</strong>
        </div>
        <div className="form-group">
          <label className="form-label">Valor a pagar (R$)</label>
          <input className="form-control" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Pagar com a conta</label>
          <select className="form-control" value={accountId} onChange={e => setAccountId(e.target.value)}>
            {payAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Data do pagamento</label>
          <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-full" onClick={pay} style={{ padding: 12 }}>
          <i className="fas fa-check"></i> Confirmar pagamento
        </button>
      </div>
    </div>
  );
}

function CardStatement({ card: initialCard, onClose }) {
  const { state } = useApp();
  // Busca o cartão ao vivo do state para refletir atualizações (ex: Pagar Fatura)
  const card = state.cards.find(c => c.id === initialCard.id) || initialCard;
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [showTxModal, setShowTxModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);

  const cats = useMemo(() => Object.fromEntries(state.categories.map(c => [c.id, c])), [state.categories]);

  const txs = useMemo(() => {
    const mm = String(month + 1).padStart(2, '0');
    const prefix = `${year}-${mm}`;
    return state.transactions
      .filter(t => t.cardId === card.id && t.date?.startsWith(prefix))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [state.transactions, card.id, year, month]);

  const totalIn  = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.value, 0);
  const totalOut = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.value, 0);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const pct = Math.min(100, Math.round((card.used / card.limit) * 100)) || 0;
  const grad = brandGrad[card.brand] || 'linear-gradient(135deg,#444,#888)';

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 540, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-handle"></div>

        {/* Header colorido */}
        <div style={{ background: grad, borderRadius: '12px 12px 0 0', padding: '18px 20px 16px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{brandLabel[card.brand] || 'Cartão'}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginTop: 2 }}>{card.name}</div>
              {card.lastDigits && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>•••• •••• •••• {card.lastDigits}</div>}
            </div>
            <button className="close-btn" onClick={onClose} style={{ color: '#fff', opacity: 0.8 }}>×</button>
          </div>

          {/* Limite / barra */}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(255,255,255,0.65)', marginBottom: 4 }}>
              <span>Fatura atual: {formatCurrency(card.used)}</span>
              <span>Limite: {formatCurrency(card.limit)}</span>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 99, height: 5, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 99, background: pct >= 90 ? '#fca5a5' : pct >= 70 ? '#fde68a' : '#a7f3d0', width: `${pct}%`, transition: 'width 0.6s' }}></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>Fecha dia {card.closeDay} · Vence dia {card.dueDay}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setShowPayModal(true)}
                  style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                >
                  <i className="fas fa-money-bill-wave" style={{ fontSize: 12 }}></i> Pagar fatura
                </button>
                <button
                  onClick={() => setShowTxModal(true)}
                  style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                >
                  <i className="fas fa-plus" style={{ fontSize: 12 }}></i> Novo lançamento
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Seletor de mês */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <button className="btn btn-ghost btn-sm" onClick={prevMonth} style={{ padding: '4px 10px' }}><i className="fas fa-chevron-left"></i></button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Fatura de {MONTH_NAMES[month]} {year}</div>
            <div style={{ fontSize: 10, color: 'var(--muted)' }}>vence dia {card.dueDay} · fecha dia {card.closeDay}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={nextMonth} style={{ padding: '4px 10px' }}><i className="fas fa-chevron-right"></i></button>
        </div>

        {/* Resumo */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Créditos</div>
            <div style={{ fontWeight: 700, color: 'var(--green)', fontSize: 13 }}>+{formatCurrency(totalIn)}</div>
          </div>
          <div style={{ textAlign: 'center', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Gastos</div>
            <div style={{ fontWeight: 700, color: 'var(--red)', fontSize: 13 }}>-{formatCurrency(totalOut)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Disponível</div>
            <div style={{ fontWeight: 700, color: 'var(--green)', fontSize: 13 }}>{formatCurrency(card.limit - card.used)}</div>
          </div>
        </div>

        {/* Lista */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px' }}>
          {txs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
              <i className="fas fa-credit-card" style={{ fontSize: 28, marginBottom: 10, display: 'block' }}></i>
              Nenhum lançamento em {MONTH_NAMES[month]}
            </div>
          ) : txs.map(tx => {
            const cat = cats[tx.categoryId];
            const isLate = tx.status === 'pending' && new Date(tx.date + 'T00:00:00') < today;
            return (
              <div key={tx.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: cat ? `${cat.color}18` : 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: cat?.color || 'var(--muted)', fontSize: 13 }}>
                  <i className={`fas ${cat?.icon || 'fa-tag'}`}></i>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.description}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                    {/* Em cartão o tx.date é o vencimento da fatura — o que interessa
                        na linha é o dia em que a compra foi feita */}
                    {tx.purchaseDate ? `Compra ${formatDate(tx.purchaseDate)}` : formatDate(tx.date)}{cat && <> · {cat.name}</>}
                  </div>
                </div>
                {tx.status === 'paid'
                  ? <span className="tag tag-green" style={{ fontSize: 10, flexShrink: 0 }}>Pago</span>
                  : isLate
                    ? <span className="tag tag-red" style={{ fontSize: 10, flexShrink: 0 }}>Atrasado</span>
                    : <span className="tag tag-amber" style={{ fontSize: 10, flexShrink: 0 }}>Pendente</span>
                }
                <div style={{ fontWeight: 700, fontSize: 14, color: tx.type === 'income' ? 'var(--green)' : 'var(--red)', flexShrink: 0 }}>
                  {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.value)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {showTxModal && (
        <TransactionModal
          tx={null}
          defaultType="expense"
          defaultCardId={card.id}
          onClose={() => setShowTxModal(false)}
        />
      )}
      {showPayModal && (
        <PayBillModal card={card} onClose={() => setShowPayModal(false)} />
      )}
    </div>
  );
}

export default function Accounts() {
  const { state, dispatch, totalBalance } = useApp();
  const [tab, setTab] = useState('accounts');
  const [modal, setModal] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [viewAcc, setViewAcc] = useState(null);
  const [viewCard, setViewCard] = useState(null);

  const activeAccounts = state.accounts.filter(a => a.active);
  const usedPct = (card) => Math.min(100, Math.round(card.used / card.limit * 100));
  const barColor = (pct) => pct >= 90 ? '#e24b4a' : pct >= 70 ? 'var(--gold)' : '#1d9e75';

  const removeAccount = (e, id) => {
    e.stopPropagation();
    const txCount = state.transactions.filter(t => t.accountId === id && !t.cardId).length;
    const msg = txCount > 0
      ? `Remover esta conta? Isso também excluirá ${txCount} transaç${txCount === 1 ? 'ão vinculada' : 'ões vinculadas'}.`
      : 'Remover esta conta?';
    if (window.confirm(msg)) {
      dispatch({ type: 'DELETE_ACCOUNT', payload: id });
      toast.info('Conta removida.');
    }
  };

  const removeCard = (e, id) => {
    e.stopPropagation();
    if (window.confirm('Remover este cartão?')) {
      dispatch({ type: 'DELETE_CARD', payload: id });
      toast.info('Cartão removido.');
    }
  };

  return (
    <div className="page-enter">

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>Contas e Cartões</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Gerencie suas contas e cartões</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {tab === 'accounts' && (
            <button className="btn btn-ghost" onClick={() => setModal('import')}>
              <i className="fas fa-file-import"></i> Importar Extrato
            </button>
          )}
          <button className="btn btn-primary" onClick={() => { setEditItem(null); setModal(tab === 'accounts' ? 'account' : 'card'); }}>
            <i className="fas fa-plus"></i> {tab === 'accounts' ? 'Nova Conta' : 'Novo Cartão'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: 'var(--surface2)', borderRadius: 10, padding: 4, gap: 4, marginBottom: 20, width: 'fit-content' }}>
        {[{ id: 'accounts', label: 'Contas Bancárias' }, { id: 'cards', label: 'Cartões de Crédito' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 20px', border: 'none', borderRadius: 8, cursor: 'pointer',
            fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
            background: tab === t.id ? 'var(--card-bg)' : 'transparent',
            color: tab === t.id ? 'var(--text)' : 'var(--muted)',
            boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── CONTAS BANCÁRIAS TAB ── */}
      {tab === 'accounts' && (
        <>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div className="card" style={{ marginBottom: 0 }}>
              <div className="metric-label">Saldo Total</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--green)', marginTop: 6 }}>{formatCurrency(totalBalance)}</div>
              {activeAccounts.some(a => a.currency === 'USD') && (
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>contas em US$ convertidas pelo câmbio do dia (R$ {state.usdRate.toFixed(2).replace('.', ',')})</div>
              )}
            </div>
            <div className="card" style={{ marginBottom: 0 }}>
              <div className="metric-label">Nº de Contas</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginTop: 6 }}>{activeAccounts.length}</div>
            </div>
          </div>

          {/* Account cards grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {activeAccounts.map(acc => (
              <div key={acc.id} style={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
                {/* Gradient top */}
                <div
                  style={{ background: accountGradients[acc.color] || 'linear-gradient(135deg,#444,#888)', padding: '20px 20px 24px', cursor: 'pointer', position: 'relative' }}
                  onClick={() => setViewAcc(acc)}
                >
                  {/* Card icon */}
                  <div style={{ position: 'absolute', top: 16, right: 16, width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-credit-card" style={{ color: '#fff', fontSize: 16 }}></i>
                  </div>

                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {typeLabel[acc.type] || 'Conta'}
                    {acc.currency === 'USD' && <span style={{ marginLeft: 8, background: 'rgba(255,255,255,0.22)', borderRadius: 5, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>🇺🇸 USD</span>}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 2 }}>{acc.name}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 20 }}>{acc.bank || ''}</div>

                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>Saldo disponível</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: acc.balance < 0 ? '#fca5a5' : '#fff' }}>{formatMoney(acc.balance, acc.currency)}</div>
                  {acc.currency === 'USD' && (
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                      ≈ {formatCurrency(toBRL(acc.balance, 'USD', state.usdRate))} · câmbio R$ {state.usdRate.toFixed(2).replace('.', ',')}
                    </div>
                  )}
                  {acc.overdraft > 0 && acc.balance < 0 && (() => {
                    const usado = Math.min(Math.abs(acc.balance), acc.overdraft);
                    const restante = acc.overdraft - usado;
                    return (
                      <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(239,68,68,0.25)', border: '1px solid rgba(239,68,68,0.4)', fontSize: 11, color: '#fca5a5' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <i className="fas fa-triangle-exclamation"></i>
                          Usando cheque especial · {formatCurrency(usado)} de {formatCurrency(acc.overdraft)}
                        </div>
                        <div style={{ marginTop: 3, color: restante > 0 ? '#fde68a' : '#fca5a5' }}>
                          Disponível: {formatCurrency(restante)}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* White footer */}
                <div style={{ background: 'var(--card-bg)', borderTop: '1px solid var(--border)', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button
                    onClick={e => { e.stopPropagation(); setEditItem(acc); setModal('account'); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 13, fontWeight: 500, padding: '2px 8px', borderRadius: 6, transition: 'background 0.12s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; e.currentTarget.style.color = 'var(--text)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--muted)'; }}
                  >
                    <i className="fas fa-pen" style={{ marginRight: 6, fontSize: 11 }}></i>Editar
                  </button>
                  <button
                    onClick={e => removeAccount(e, acc.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13, fontWeight: 500, padding: '2px 8px', borderRadius: 6, transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <i className="fas fa-trash-can" style={{ marginRight: 6, fontSize: 11 }}></i>Remover
                  </button>
                </div>
              </div>
            ))}

            {/* Add account card */}
            <div
              onClick={() => { setEditItem(null); setModal('account'); }}
              style={{ borderRadius: 'var(--radius)', border: '2px dashed var(--border)', minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)', gap: 8, transition: 'border-color 0.15s, color 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--navy)'; e.currentTarget.style.color = 'var(--navy)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--muted)'; }}
            >
              <i className="fas fa-plus" style={{ fontSize: 24 }}></i>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Nova conta</span>
            </div>
          </div>

        </>
      )}

      {/* ── CARTÕES DE CRÉDITO TAB ── */}
      {tab === 'cards' && (
        <>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div className="card" style={{ marginBottom: 0 }}>
              <div className="metric-label">Total em faturas</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--red)', marginTop: 6 }}>{formatCurrency(state.cards.reduce((s, c) => s + c.used, 0))}</div>
            </div>
            <div className="card" style={{ marginBottom: 0 }}>
              <div className="metric-label">Limite disponível</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--green)', marginTop: 6 }}>{formatCurrency(state.cards.reduce((s, c) => s + (c.limit - c.used), 0))}</div>
            </div>
          </div>

          {/* Credit cards grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {state.cards.map(card => {
              const pct = usedPct(card);
              const bg = card.brand === 'mastercard' ? 'linear-gradient(135deg,#eb001b,#f79e1b)' :
                         card.brand === 'visa'       ? 'linear-gradient(135deg,#1a1f71,#4a5abd)' :
                         card.brand === 'elo'        ? 'linear-gradient(135deg,#111,#444)' :
                                                       'linear-gradient(135deg,#007bc1,#00b9f2)';
              return (
                <div key={card.id} style={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
                  {/* Gradient top */}
                  <div style={{ background: bg, padding: '20px 20px 20px', cursor: 'pointer', position: 'relative' }} onClick={() => setViewCard(card)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                      <div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                          {brandLabel[card.brand] || 'Cartão'}
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>{card.name}</div>
                        {card.lastDigits && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>•••• •••• •••• {card.lastDigits}</div>}
                      </div>
                      <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fas fa-credit-card" style={{ color: '#fff', fontSize: 16 }}></i>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>
                      <span>Fatura atual</span>
                      <span>{formatCurrency(card.used)} / {formatCurrency(card.limit)}</span>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 99, height: 5, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 99, background: pct >= 90 ? '#fca5a5' : pct >= 70 ? '#fde68a' : '#a7f3d0', width: `${pct}%`, transition: 'width 0.6s' }}></div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 6 }}>
                      <span>Fecha dia {card.closeDay} · Vence dia {card.dueDay}</span>
                      <span className={`tag ${pct >= 90 ? 'tag-red' : pct >= 70 ? 'tag-amber' : 'tag-green'}`} style={{ fontSize: 10 }}>{pct}%</span>
                    </div>
                  </div>

                  {/* White footer */}
                  <div style={{ background: 'var(--card-bg)', borderTop: '1px solid var(--border)', padding: '8px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 6, gap: 6 }}>
                      <i className="fas fa-wallet" style={{ fontSize: 11, color: (card.limit - card.used) > 0 ? '#1d9e75' : '#e24b4a' }}></i>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>Disponível:</span>
                      <strong style={{ fontSize: 13, color: (card.limit - card.used) > 0 ? '#1d9e75' : '#e24b4a' }}>
                        {formatCurrency(card.limit - card.used)}
                      </strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <button
                        onClick={e => { e.stopPropagation(); setEditItem(card); setModal('card'); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 13, fontWeight: 500, padding: '2px 8px', borderRadius: 6, transition: 'background 0.12s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; e.currentTarget.style.color = 'var(--text)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--muted)'; }}
                      >
                        <i className="fas fa-pen" style={{ marginRight: 6, fontSize: 11 }}></i>Editar
                      </button>
                      <button
                        onClick={e => removeCard(e, card.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13, fontWeight: 500, padding: '2px 8px', borderRadius: 6, transition: 'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <i className="fas fa-trash-can" style={{ marginRight: 6, fontSize: 11 }}></i>Remover
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add card placeholder */}
            <div
              onClick={() => { setEditItem(null); setModal('card'); }}
              style={{ borderRadius: 'var(--radius)', border: '2px dashed var(--border)', minHeight: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)', gap: 8, transition: 'border-color 0.15s, color 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--navy)'; e.currentTarget.style.color = 'var(--navy)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--muted)'; }}
            >
              <i className="fas fa-plus" style={{ fontSize: 24 }}></i>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Novo cartão</span>
            </div>
          </div>
        </>
      )}

      {modal === 'account' && <AccountModal account={editItem} onClose={() => setModal(null)} />}
      {modal === 'card' && <CardModal card={editItem} onClose={() => setModal(null)} />}
      {modal === 'import' && <ImportModal onClose={() => setModal(null)} />}
      {viewAcc && <AccountStatement account={viewAcc} onClose={() => setViewAcc(null)} />}
      {viewCard && <CardStatement card={viewCard} onClose={() => setViewCard(null)} />}
    </div>
  );
}
